import React, { useState, useEffect } from 'react';
import {
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

/**
 * ParameterValueInput Component
 *
 * A reusable component for parameter value input fields that:
 * - Shows a regular TextField for short strings
 * - Shows a TextField with an edit button for long strings
 * - Opens a dialog with "show more/less" functionality for viewing/editing long content
 *
 * @param {string} value - The current value
 * @param {function} onChange - Callback when value changes
 * @param {string} label - Label for the input field
 * @param {string} placeholder - Placeholder text
 * @param {boolean} required - Whether the field is required
 * @param {number} longTextThreshold - Character count threshold to show edit button (default: 80)
 */
const ParameterValueInput = ({
  value = '',
  onChange,
  label,
  placeholder,
  required = false,
  longTextThreshold = 80,
  ...otherProps
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState(value);
  const [isExpanded, setIsExpanded] = useState(false);

  const stringValue = String(value || '');
  const isLongText = stringValue.length > longTextThreshold;

  // Sync dialog value when prop value changes (but not when dialog is open)
  useEffect(() => {
    if (!dialogOpen) {
      setDialogValue(stringValue);
    }
  }, [stringValue, dialogOpen]);

  const handleOpenDialog = () => {
    setDialogValue(stringValue);
    setIsExpanded(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSaveDialog = () => {
    onChange(dialogValue);
    setDialogOpen(false);
  };

  const handleCancelDialog = () => {
    setDialogValue(stringValue);
    setDialogOpen(false);
  };

  // Determine if we should show "Show more" button (more than ~30 words or 200 chars)
  const shouldShowMoreButton = text => {
    if (!text) return false;
    const wordCount = text
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0).length;
    return wordCount > 30 || text.length > 200;
  };

  return (
    <>
      <Box
        sx={{ position: 'relative', width: '100%', ...(otherProps.sx || {}) }}
      >
        <TextField
          value={stringValue}
          onChange={e => onChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          required={required}
          size="small"
          fullWidth
          multiline={isLongText}
          rows={isLongText ? 2 : 1}
          sx={{
            '& .MuiInputBase-input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              paddingRight: isLongText ? '40px' : undefined,
            },
            ...(otherProps.sx || {}),
          }}
          {...Object.fromEntries(
            Object.entries(otherProps).filter(([key]) => key !== 'sx')
          )}
        />
        {isLongText && (
          <IconButton
            size="small"
            onClick={handleOpenDialog}
            sx={{
              position: 'absolute',
              right: '8px',
              top: isLongText ? '32px' : '50%',
              transform: isLongText ? 'none' : 'translateY(-50%)',
              zIndex: 1,
            }}
            title="Edit in dialog"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Edit Dialog for Long Text */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6">{label}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <TextField
              fullWidth
              multiline
              rows={isExpanded ? 20 : 4}
              value={dialogValue}
              onChange={e => setDialogValue(e.target.value)}
              placeholder={placeholder}
              sx={{
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  resize: 'none',
                },
                '& textarea': {
                  overflow: 'hidden !important',
                  resize: 'none',
                },
              }}
            />
            {shouldShowMoreButton(dialogValue) && (
              <Button
                onClick={() => setIsExpanded(!isExpanded)}
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  color: 'primary.main',
                  minWidth: 'auto',
                  px: 1,
                }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ParameterValueInput;
